import * as actions from 'actions/dashBuilder'
import { GET_TRUCK_DETAILS, TRUCK_DETAILS_NOT_RECEIVED } from 'actions/truck'
import { fromJS } from 'immutable'
import strings from 'utils/strings'
import { set, setIn } from 'utils/immutable'
import { length, head, compose } from 'ramda'
import { CURRENT_DATE } from 'utils/utils'
import { plantLocations } from 'constants/dash'
import { createReducer } from './utils'
import {
  CLEVELAND,
  FCCC,
  MT_HOLLY,
  PORTLAND,
  SANTIAGO,
} from '../constants/dash'

export const initialState = fromJS({
  searchValue: '',
  serialNumber: '',
  dashboardType: '',
  dataBookCodes: [],
  resultFound: null,
  isLeadSerial: null,
  isPreProduction: false,
  isAftermarket: false,
  leadSerialNumber: '',
  buildDate: '',
  dashboard: {
    searchVehicle: null,
    selectedScheduleDate: CURRENT_DATE,
    selectedLocation: `${strings.select_plant}`,
    locations: [CLEVELAND, FCCC, MT_HOLLY, PORTLAND, SANTIAGO],
    locationDropdown: [
      {
        title: CLEVELAND,
        value: CLEVELAND,
        key: CLEVELAND,
      },
      {
        title: FCCC,
        value: FCCC,
        key: FCCC,
      },
      {
        title: MT_HOLLY,
        value: MT_HOLLY,
        key: MT_HOLLY,
      },
      {
        title: PORTLAND,
        value: PORTLAND,
        key: PORTLAND,
      },
      {
        title: SANTIAGO,
        value: SANTIAGO,
        key: SANTIAGO,
      },
    ],
    customLayoutSearch: false,
    scheduleReceived: false,
    scheduleError: false,
    productionScheduleRequestId: 0,
    productionSchedule: null,
    collectionName: '',
    total: 0,
    startIndex: 0,
    pageData: null,
    isSearching: false,

    dashBuilderSearchInput: null,
    selectedVehicleData: null,
    selectedDashType: null,
    dashMeta: null,
    dashBlackList: null,
    dataBookCodes: [],
    overHeadPanelConfigs: null,
    subType: null,
    resultsFound: null,
  },
})

const handlers = {
  [actions.SET_DASH_BUILDER_SEARCH_INPUT]: setDashBuilderSearchInput,
  [actions.SET_PRODUCTION_SCHEDULE_DATE]: setProductionScheduleDate,
  [actions.SET_SELECTED_LOCATION_DROP_DOWN]: setSelectedLocation,
  [actions.TOGGLE_CUSTOM_LAYOUT_SEARCH]: toggleCustomLayoutSearch,
  [actions.UPDATE_PROD_SCHEDULE_FROM_API_RESPONSE]:
    productionScheduleResponseReceived,
  [actions.PRODUCTION_SCHEDULE_PAGE_FAILED]: productionSchedulePageFailed,
  [actions.NEXT_PRODUCTION_SCHEDULE_REQUEST_ID]:
    nextProductionScheduleRequestId,
  [actions.UPDATE_SELECTED_VEHICLE_DATA]: updateSelectedVehicleData,
  [actions.UPDATE_SELECTED_DASH_TYPE]: updateSelectedDashType,
  [actions.UPDATE_DASH_VIEW]: updateDashView,
  [actions.UPDATE_PAGINATED_PRODUCTION_SCHEDULE]:
    updatePaginatedProductionSchedule,
  [actions.REQUESTING_DASH_BUILDER_SEARCH]: requestDashBuilderSearch,
  [actions.SUCCESS_DASH_BUILDER_SEARCH]: successDashBuilderSearch,
  [actions.FAILURE_DASH_BUILDER_SEARCH]: failureDashBuilderSearch,
  [actions.SET_DASH_BUILDER_SEARCH_VALUE]: setDashBuilderSearchValue,
  [actions.SET_DASH_BUILDER_FOUND_RESULT]: setDashBuilderResultFound,
  [GET_TRUCK_DETAILS]: syncVehicleFlagsFromTruckDetails,
  [TRUCK_DETAILS_NOT_RECEIVED]: handleTruckDetailsFailure,
}

export default createReducer(initialState, handlers)

function updateDashView(
  state,
  {
    serial,
    dashType,
    dataBookCodes,
    isLeadSerial,
    leadSerialNumber,
    isPreProduction,
    isAftermarket,
  }
) {
  const transform = compose(
    set('serialNumber', serial),
    set('dashboardType', dashType),
    set('dataBookCodes', dataBookCodes),
    set('isLeadSerial', Boolean(isLeadSerial)),
    set('leadSerialNumber', leadSerialNumber || ''),
    set('isPreProduction', Boolean(isPreProduction)),
    set('isAftermarket', Boolean(isAftermarket)),
    set('resultFound', true)
  )
  return transform(state)
}

function updatePaginatedProductionSchedule(state, { paginatedSchedule }) {
  const {
    pageData,
    vehicles,
    total,
    date,
    location,
    customLayoutSearch,
    startIndex,
  } = paginatedSchedule
  const convertLocation = plantLocations[location]
  const transform = compose(
    setIn(['dashboard', 'total'], total),
    setIn(['dashboard', 'startIndex'], startIndex),
    setIn(['dashboard', 'pageData'], pageData),
    setIn(['dashboard', 'selectedScheduleDate'], date),
    setIn(['dashboard', 'selectedLocation'], convertLocation),
    setIn(['dashboard', 'customLayoutSearch'], customLayoutSearch),
    setIn(['dashboard', 'productionSchedule'], vehicles)
  )
  return transform(state)
}

function setDashBuilderSearchInput(state, { dashBuilderSearchInput }) {
  const transform = compose(
    setIn(['dashboard', 'dashBuilderSearchInput'], dashBuilderSearchInput)
  )
  return transform(state)
}

function setProductionScheduleDate(state, { selectedScheduleDate }) {
  const transform = compose(
    setIn(['dashboard', 'selectedScheduleDate'], selectedScheduleDate)
  )
  return transform(state)
}

function setSelectedLocation(state, { selectedLocation }) {
  const transform = compose(
    setIn(['dashboard', 'selectedLocation'], selectedLocation)
  )
  return transform(state)
}

function toggleCustomLayoutSearch(state, { customLayoutSearch }) {
  const transform = compose(
    setIn(['dashboard', 'customLayoutSearch'], customLayoutSearch)
  )
  return transform(state)
}

function productionScheduleResponseReceived(
  state,
  { vehicles, total, collectionName }
) {
  const transform = compose(
    setIn(['dashboard', 'productionSchedule'], vehicles),
    setIn(['dashboard', 'scheduleReceived'], true),
    setIn(['dashboard', 'scheduleError'], false),
    setIn(['dashboard', 'collectionName'], collectionName),
    setIn(['dashboard', 'total'], total)
  )
  return transform(state)
}

// Stamps the next production-schedule request. The id travels with the request
// and comes back on its response, so watchProductionScheduleResult can tell a
// current answer from one belonging to a query the user already moved on from.
function nextProductionScheduleRequestId(state) {
  const current = state.getIn(['dashboard', 'productionScheduleRequestId']) || 0
  return setIn(['dashboard', 'productionScheduleRequestId'], current + 1, state)
}

// The page the user is currently on failed to load. Drop the previous page's
// vehicles instead of leaving them rendered under the new page's range — stale
// rows read as duplicated and missing build sequence numbers. `total` is left
// untouched so the paginator stays usable and the user can retry or move on.
//
// Only reached via watchProductionScheduleResult, which discards responses
// belonging to a request the user has already navigated away from.
function productionSchedulePageFailed(state) {
  const transform = compose(
    setIn(['dashboard', 'productionSchedule'], []),
    setIn(['dashboard', 'scheduleReceived'], true),
    setIn(['dashboard', 'scheduleError'], true)
  )
  return transform(state)
}

function updateSelectedVehicleData(
  state,
  { selectedVehicleData, dataBookCodes, selectedDashType, subType }
) {
  const transform = compose(
    setIn(['dashboard', 'selectedVehicleData'], selectedVehicleData),
    setIn(['dashboard', 'selectedDashType'], selectedDashType),
    setIn(['dashboard', 'dataBookCodes'], dataBookCodes),
    setIn(['dashboard', 'subType'], subType)
  )
  return transform(state)
}

function updateSelectedDashType(state, { selectedDashType }) {
  const transform = compose(
    setIn(['dashboard', 'selectedDashType'], selectedDashType)
  )
  return transform(state)
}

function setDashBuilderResultFound(state, { resultFound }) {
  return set('resultFound', resultFound, state)
}

function setDashBuilderSearchValue(state, { searchValue }) {
  return set('searchValue', searchValue, state)
}

function requestDashBuilderSearch(state) {
  const transform = compose(
    set('serialNumber', ''),
    set('dashboardType', ''),
    set('dataBookCodes', ''),
    set('resultFound', null),
    set('isLeadSerial', null),
    set('buildDate', ''),
    set('isPreProduction', false),
    set('isAftermarket', false),
    set('leadSerialNumber', '')
  )

  return transform(state)
}

function successDashBuilderSearch(state, action) {
  const { data = {}, requestBody: { search } = {} } = action
  const vehicles = action.vehicles || data.vehicles || []

  if (length(vehicles)) {
    const vehicle = head(vehicles)

    const { serialNumber, dashboardType, buildDate, isAftermarket } = vehicle

    const transform = compose(
      set('searchValue', search),
      set('serialNumber', serialNumber),
      set('dashboardType', dashboardType),
      set('buildDate', buildDate),
      set('isAftermarket', Boolean(isAftermarket)),
      set('resultFound', true)
    )

    return transform(state)
  }

  const transform = compose(
    set('searchValue', search),
    set('resultFound', false)
  )

  return transform(state)
}

function failureDashBuilderSearch(state) {
  // If getTruckDetails already resolved (isLeadSerial is not null) AND
  // the vehicle is NOT pre-production, it's aftermarket (exists but excluded from search)
  const vehicleDetailsLoaded = state.get('isLeadSerial') !== null
  const isNotPreProd = state.get('isPreProduction') === false
  if (vehicleDetailsLoaded && isNotPreProd) {
    return compose(set('resultFound', false), set('isAftermarket', true))(state)
  }
  return set('resultFound', false, state)
}

function syncVehicleFlagsFromTruckDetails(state, { data = {} }) {
  const { isLeadSerial, isPreProduction, leadSerialNumber, isAftermarket } =
    data
  // If the API explicitly provides isAftermarket, use it.
  // Otherwise, infer aftermarket: vehicle exists (this handler ran) AND
  // dash builder search returned 404 (resultFound === false) AND
  // the vehicle is NOT pre-production.
  const resolvedIsAftermarket =
    isAftermarket !== undefined
      ? Boolean(isAftermarket)
      : state.get('resultFound') === false && isPreProduction === false
  const transform = compose(
    set('isLeadSerial', Boolean(isLeadSerial)),
    set('isPreProduction', Boolean(isPreProduction)),
    set('leadSerialNumber', leadSerialNumber || ''),
    set('isAftermarket', resolvedIsAftermarket)
  )
  return transform(state)
}

function handleTruckDetailsFailure(state, action) {
  // When getTruckDetails fails (e.g. 404 for invalid serial), explicitly mark
  // isLeadSerial as false so the UI doesn't stay in an ambiguous null state.
  // Only act when the failed serial matches the active Dash Builder serial.
  // Ignore failures without a serial or for a different vehicle.
  const failedSerial =
    (action.args && action.args.serialNumber) ||
    (action.actionArguments && action.actionArguments.serialNumber)
  const activeSerial = state.get('serialNumber')
  if (!failedSerial || !activeSerial || failedSerial !== activeSerial) {
    return state
  }
  if (state.get('isLeadSerial') === null) {
    return set('isLeadSerial', false, state)
  }
  return state
}
